/**
 * computeInsertionPosition — midpoint-trick math for sorting kanban cards.
 *
 * When a card is dropped between two existing cards, we give it a position
 * that's the average of its neighbors. This avoids re-numbering every card
 * on every move (LinkedList-in-a-list pattern). If appending to end or
 * inserting at the start, use a +1000/-1000 offset to leave headroom.
 *
 * Per FWV v8 §30: pure function extracted from TodayView so the math is
 * testable in isolation and the surface stays focused on layout.
 */
import type { DbTask } from '../../db'

export function computeInsertionPosition(
  destTasks: DbTask[],
  insertionIndex: number
): number {
  if (destTasks.length === 0) {
    // Empty column: just use a high starting number
    return 1000
  }
  // destTasks is expected to be sorted by position ascending
  if (insertionIndex <= 0) {
    // Insert at the very top: give a position below the first card
    return destTasks[0].position - 1000
  }
  if (insertionIndex >= destTasks.length) {
    // Insert at the very end: give a position above the last card
    return destTasks[destTasks.length - 1].position + 1000
  }
  // Insert in the middle: midpoint of the two neighbors
  const before = destTasks[insertionIndex - 1]
  const after = destTasks[insertionIndex]
  return (before.position + after.position) / 2
}